import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { X, ArrowRight, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISMISS_KEY = 'dowurk-next-step-card-dismissed';
const COLLAPSE_KEY = 'dowurk-next-step-card-collapsed';
const PERSONA_KEY = 'dowurk-persona';

// Routes where the card would compete with in-flow UI or duplicate content.
const HIDDEN_ROUTES = [
  '/next-step',
  '/onboarding',
  '/auth/callback',
  '/join',
];

const HIDDEN_PREFIXES = [
  '/admin',
  '/studio',
  '/tenant',
  '/login',
  '/register',
];

/**
 * NextBestStepCard — persistent, top-right, personalized nudge for logged-in
 * users. Reads `/api/next-step` if available, otherwise falls back to the
 * saved persona from PersonaFunnelHero to suggest a reasonable next action.
 *
 * Users can collapse (session) or dismiss (24h). Both preferences persist
 * in localStorage.
 */

function isHidden(pathname) {
  if (pathname === '/') return true; // homepage has its own banner
  if (HIDDEN_ROUTES.includes(pathname)) return true;
  return HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
}

function readDismissal() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const ts = parseInt(raw, 10);
    if (Date.now() - ts > 24 * 60 * 60 * 1000) return null;
    return ts;
  } catch {
    return null;
  }
}

const PERSONA_FALLBACKS = {
  founder: {
    title: 'Finish setting up your org profile',
    body: 'Founders with a completed profile get 3× more lender + grant matches.',
    cta: { to: '/profile', label: 'Complete org profile' },
  },
  nonprofit: {
    title: 'Apply for PROOF Free',
    body: 'Publish verified impact your funders can see. Takes about 6 minutes.',
    cta: { to: '/nonprofits/signup', label: 'Start PROOF application' },
  },
  helper: {
    title: 'Log a volunteer hour',
    body: 'Every hour is proofed, receipted, and moves your PROOF badge forward.',
    cta: { to: '/volunteer-hub', label: 'Find a shift' },
  },
  default: {
    title: 'Try the DowUrk Score',
    body: 'See how ready your org is for capital, compliance, and community trust.',
    cta: { to: '/dowurk-score', label: 'Take the 2-min assessment' },
  },
};

export default function NextBestStepCard() {
  const { user } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => !!readDismissal());
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [nextStep, setNextStep] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch personalized suggestion from backend when authed
  useEffect(() => {
    let cancelled = false;
    if (!user) { setLoading(false); return; }
    // App auth uses httpOnly cookies (see AuthContext) — pass withCredentials
    // so the backend can identify the user via the session cookie.
    axios
      .get(`${API}/next-step/me`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const primary = res.data?.primary;
        if (primary && primary.headline) {
          setNextStep({
            title: primary.headline,
            body: primary.detail || '',
            cta: {
              to: primary.primary_cta?.to || '/next-step',
              label: primary.primary_cta?.label || 'Take next step',
            },
          });
        }
      })
      .catch(() => { /* silent — fall back to persona */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
  };

  const handleToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // Bail-outs
  if (!user) return null;
  if (dismissed) return null;
  if (isHidden(location.pathname)) return null;
  if (loading) return null;

  const persona = (() => {
    try { return localStorage.getItem(PERSONA_KEY); } catch { return null; }
  })();
  const step = nextStep || PERSONA_FALLBACKS[persona] || PERSONA_FALLBACKS.default;

  return (
    <AnimatePresence>
      <motion.aside
        key="next-best-step-card"
        initial={{ opacity: 0, y: -10, x: 10 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="fixed top-20 right-4 z-40 w-[300px] max-w-[calc(100vw-2rem)] hidden lg:block"
        data-testid="next-best-step-card"
      >
        <div className="rounded-2xl border-2 border-emerald-300 bg-white shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 px-4 py-2.5 flex items-center justify-between gap-2 text-white">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" /> Your next step
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleToggle}
                className="p-1 hover:bg-white/10 rounded"
                aria-label={collapsed ? 'Expand' : 'Collapse'}
                data-testid="next-best-step-toggle"
              >
                {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/10 rounded"
                aria-label="Dismiss"
                data-testid="next-best-step-dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {!collapsed && (
            <div className="p-4 space-y-3">
              <p className="text-sm font-bold text-slate-900 leading-tight" data-testid="next-best-step-title">
                {step.title}
              </p>
              {step.body && (
                <p className="text-xs text-slate-600 leading-snug">{step.body}</p>
              )}
              <Link
                to={step.cta.to}
                className="w-full inline-flex items-center justify-between gap-2 bg-gradient-to-r from-[#006847] to-[#1E4D2E] hover:opacity-90 text-white font-semibold text-sm rounded-lg px-3 py-2 shadow-sm"
                data-testid="next-best-step-cta"
              >
                <span>{step.cta.label}</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/next-step"
                className="block text-center text-[11px] text-slate-500 hover:text-emerald-700 font-medium"
                data-testid="next-best-step-see-all"
              >
                See all my next steps →
              </Link>
            </div>
          )}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}
